import React, { useState, useEffect } from 'react';
import { Space, Card, Button, Tabs, DatePicker, Table, Tag, Modal, Form, message, Typography, Row, Col, Select, Grid } from 'antd';
import { FileTextOutlined, LockOutlined, UnlockOutlined, PlusOutlined, DollarOutlined, BankOutlined, WalletOutlined, ToolOutlined, BarChartOutlined, AppstoreOutlined, CalendarOutlined } from '@ant-design/icons';
import client from '../api/client';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const StatCard = ({ title, value, prefix, icon, color, subText }) => (
    <Card className="premium-card" bodyStyle={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: parseFloat(value) < 0 ? '#ef4444' : 'var(--text-primary)' }}>
                    {prefix}{parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Title>
                {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
            </div>
            <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
        </div>
    </Card>
);

const FinancialReportsTab = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [reportType, setReportType] = useState('income-statement');
    const [dates, setDates] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const exportCsv = () => {
        if (!data) {
            message.info('No report data to export');
            return;
        }

        const escapeValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
        const rows = [];

        if (reportType === 'trial-balance') {
            rows.push(['Report', 'Trial Balance']);
            rows.push(['Period', `${dates[0].format('YYYY-MM-DD')} to ${dates[1].format('YYYY-MM-DD')}`]);
            rows.push([]);
            rows.push(['Account Code', 'Account Name', 'Type', 'Debit', 'Credit', 'Balance']);
            data.forEach((item) => {
                rows.push([item.code, item.name, item.type, item.total_debit, item.total_credit, item.balance]);
            });
        } else if (reportType === 'income-statement') {
            rows.push(['Report', 'Profit & Loss (Income Statement)']);
            rows.push(['Period', `${dates[0].format('YYYY-MM-DD')} to ${dates[1].format('YYYY-MM-DD')}`]);
            rows.push([]);
            rows.push(['Income']);
            rows.push(['Account', 'Amount']);
            data.income.forEach((item) => {
                rows.push([item.name, item.balance]);
            });
            rows.push(['Total Income', data.total_income]);
            rows.push([]);
            rows.push(['Cost of Goods Sold']);
            rows.push(['Account', 'Amount']);
            data.cogs.forEach((item) => {
                rows.push([item.name, item.balance]);
            });
            rows.push(['Total COGS', data.total_cogs]);
            rows.push([]);
            rows.push(['Gross Profit', data.gross_profit]);
            rows.push([]);
            rows.push(['Operating Expenses']);
            rows.push(['Account', 'Amount']);
            data.expenses.forEach((item) => {
                rows.push([item.name, item.balance]);
            });
            rows.push(['Total Operating Expenses', data.total_expense]);
            rows.push([]);
            rows.push(['Net Income', data.net_income]);
        } else {
            rows.push(['Report', 'Balance Sheet']);
            rows.push(['As Of', dates[1].format('YYYY-MM-DD')]);
            rows.push([]);
            rows.push(['Assets']);
            rows.push(['Account', 'Amount']);
            data.assets.forEach((asset) => {
                rows.push([asset.name, asset.balance]);
            });
            rows.push([]);
            rows.push(['Liabilities']);
            rows.push(['Account', 'Amount']);
            data.liabilities.forEach((liability) => {
                rows.push([liability.name, liability.balance]);
            });
            rows.push([]);
            rows.push(['Equity']);
            rows.push(['Account', 'Amount']);
            data.equity.forEach((eq) => {
                rows.push([eq.name, eq.balance]);
            });
            rows.push([]);
            rows.push(['Total Assets', data.total_assets]);
            rows.push(['Retained Earnings', data.retained_earnings_calced]);
            rows.push(['Total Liabilities + Equity', data.total_liabilities_equity]);
        }

        const csv = rows
            .map((row) => row.map(escapeValue).join(','))
            .join('\n');
        const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const dateLabel = `${dates[0].format('YYYY-MM-DD')}_to_${dates[1].format('YYYY-MM-DD')}`;
        link.href = url;
        link.setAttribute('download', `${reportType}-${dateLabel}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const generate = async () => {
        setLoading(true);
        try {
            let url = '';
            if (reportType === 'trial-balance') url = '/transactions/reports/trial-balance';
            else if (reportType === 'income-statement') url = '/transactions/reports/income-statement';
            else url = '/transactions/reports/balance-sheet';

            const params = {
                start_date: dates[0].format('YYYY-MM-DD'),
                end_date: dates[1].format('YYYY-MM-DD'),
                as_of_date: dates[1].format('YYYY-MM-DD')
            };
            const { data } = await client.get(url, { params });
            setData(data);
        } catch (error) {
            message.error('Failed to generate report');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--card-bg)', padding: 24, borderRadius: 16, border: '1px solid rgba(148, 163, 184, 0.2)' }}>
                <Space size="middle">
                    <Button
                        type={reportType === 'trial-balance' ? 'primary' : 'default'}
                        onClick={() => { setReportType('trial-balance'); setData(null); }}
                        style={{ height: 40, borderRadius: 8 }}
                    >
                        Trial Balance
                    </Button>
                    <Button
                        type={reportType === 'income-statement' ? 'primary' : 'default'}
                        onClick={() => { setReportType('income-statement'); setData(null); }}
                        style={{ height: 40, borderRadius: 8 }}
                    >
                        Income Statement
                    </Button>
                    <Button
                        type={reportType === 'balance-sheet' ? 'primary' : 'default'}
                        onClick={() => { setReportType('balance-sheet'); setData(null); }}
                        style={{ height: 40, borderRadius: 8 }}
                    >
                        Balance Sheet
                    </Button>
                </Space>
                <Space size="middle">
                    <RangePicker value={dates} onChange={setDates} style={{ height: 40, borderRadius: 8 }} />
                    <Button
                        type="primary"
                        icon={<FileTextOutlined />}
                        onClick={generate}
                        loading={loading}
                        style={{ height: 40, borderRadius: 8, fontWeight: 600 }}
                    >
                        Generate Report
                    </Button>
                    <Button
                        onClick={exportCsv}
                        style={{ height: 40, borderRadius: 8, fontWeight: 600 }}
                    >
                        Export CSV
                    </Button>
                </Space>
            </div>

            {data && reportType === 'trial-balance' && (
                <div style={{ marginTop: 32 }}>
                    <Table
                        dataSource={data}
                        pagination={false}
                        bordered
                        size="middle"
                        rowKey="code"
                        columns={[
                            { title: 'Code', dataIndex: 'code', width: 100 },
                            { title: 'Account Name', dataIndex: 'name' },
                            { title: 'Type', dataIndex: 'type', render: (type) => <Tag color="blue">{type}</Tag> },
                            { title: 'Debit', dataIndex: 'total_debit', align: 'right', render: (val) => Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                            { title: 'Credit', dataIndex: 'total_credit', align: 'right', render: (val) => Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                            { title: 'Balance', dataIndex: 'balance', align: 'right', render: (val) => <strong>₱{Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong> },
                        ]}
                        summary={(pageData) => {
                            let totalDebit = 0;
                            let totalCredit = 0;
                            pageData.forEach(({ total_debit, total_credit }) => {
                                totalDebit += Number(total_debit);
                                totalCredit += Number(total_credit);
                            });
                            return (
                                <Table.Summary.Row style={{ backgroundColor: '#f8fafc', fontWeight: 600 }}>
                                    <Table.Summary.Cell index={0} colSpan={3}>TOTAL</Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="right">₱{totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="right">₱{totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} />
                                </Table.Summary.Row>
                            );
                        }}
                    />
                </div>
            )}

            {data && reportType === 'income-statement' && (
                <Card className="premium-card" style={{ marginTop: 32, maxWidth: 900, margin: '32px auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 40, paddingTop: 20 }}>
                        <Title level={3} style={{ margin: 0, textTransform: 'uppercase', letterSpacing: 1 }}>Profit & Loss</Title>
                        <Typography.Text type="secondary" style={{ fontSize: 16 }}>{dates[0].format('MMMM D, YYYY')} - {dates[1].format('MMMM D, YYYY')}</Typography.Text>
                    </div>

                    <div className="quickbooks-report" style={{ padding: '0 40px 40px 40px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', padding: '12px 0', borderBottom: '2px solid #e2e8f0', color: 'var(--text-secondary)', textTransform: 'uppercase', fontSize: 12 }}>Account</th>
                                    <th style={{ textAlign: 'right', padding: '12px 0', borderBottom: '2px solid #e2e8f0', color: 'var(--text-secondary)', textTransform: 'uppercase', fontSize: 12, width: 150 }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Income Section */}
                                <tr>
                                    <td colSpan={2} style={{ padding: '24px 0 8px 0', fontWeight: 700, textTransform: 'uppercase', fontSize: 13, color: '#64748b' }}>Income</td>
                                </tr>
                                {(data.income || []).map(acc => (
                                    <tr key={acc.code}>
                                        <td style={{ padding: '6px 24px', color: 'var(--text-primary)' }}>{acc.code} · {acc.name}</td>
                                        <td style={{ textAlign: 'right', padding: '6px 0', fontFamily: 'monospace', fontSize: 15 }}>₱{Number(acc.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                ))}
                                <tr style={{ fontWeight: 700 }}>
                                    <td style={{ padding: '16px 0', borderTop: '1px solid #e2e8f0' }}>Total Income</td>
                                    <td style={{ textAlign: 'right', padding: '16px 0', borderTop: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 15 }}>₱{Number(data.total_income).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>

                                {/* COGS Section */}
                                <tr>
                                    <td colSpan={2} style={{ padding: '32px 0 8px 0', fontWeight: 700, textTransform: 'uppercase', fontSize: 13, color: '#64748b' }}>Cost of Goods Sold</td>
                                </tr>
                                {(data.cogs || []).map(acc => (
                                    <tr key={acc.code}>
                                        <td style={{ padding: '6px 24px', color: 'var(--text-primary)' }}>{acc.code} · {acc.name}</td>
                                        <td style={{ textAlign: 'right', padding: '6px 0', fontFamily: 'monospace', fontSize: 15 }}>₱{Number(acc.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                ))}
                                <tr style={{ fontWeight: 700 }}>
                                    <td style={{ padding: '16px 0', borderTop: '1px solid #e2e8f0' }}>Total COGS</td>
                                    <td style={{ textAlign: 'right', padding: '16px 0', borderTop: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 15 }}>₱{Number(data.total_cogs).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>

                                {/* Gross Profit */}
                                <tr style={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>
                                    <td style={{ padding: '20px 12px' }}>GROSS PROFIT</td>
                                    <td style={{ textAlign: 'right', padding: '20px 12px', borderTop: '2px solid #cbd5e1', borderBottom: '2px solid #cbd5e1', fontFamily: 'monospace', fontSize: 16 }}>₱{Number(data.gross_profit).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>

                                {/* Expense Section */}
                                <tr>
                                    <td colSpan={2} style={{ padding: '32px 0 8px 0', fontWeight: 700, textTransform: 'uppercase', fontSize: 13, color: '#64748b' }}>Expenses</td>
                                </tr>
                                {(data.expenses || []).map(acc => (
                                    <tr key={acc.code}>
                                        <td style={{ padding: '6px 24px', color: 'var(--text-primary)' }}>{acc.code} · {acc.name}</td>
                                        <td style={{ textAlign: 'right', padding: '6px 0', fontFamily: 'monospace', fontSize: 15 }}>₱{Number(acc.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    </tr>
                                ))}
                                <tr style={{ fontWeight: 700 }}>
                                    <td style={{ padding: '16px 0', borderTop: '1px solid #e2e8f0' }}>Total Expenses</td>
                                    <td style={{ textAlign: 'right', padding: '16px 0', borderTop: '1px solid #e2e8f0', fontFamily: 'monospace', fontSize: 15 }}>₱{Number(data.total_expense).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>

                                {/* Net Income */}
                                <tr style={{ fontWeight: 900 }}>
                                    <td style={{ padding: '32px 0 12px 0', fontSize: 20, textTransform: 'uppercase' }}>Net Income</td>
                                    <td style={{ textAlign: 'right', padding: '32px 0 12px 0', fontSize: 22, color: data.net_income >= 0 ? '#059669' : '#dc2626', borderBottom: '4px double #000', fontFamily: 'monospace' }}>
                                        ₱{Number(data.net_income).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {data && reportType === 'balance-sheet' && (
                <div style={{ marginTop: 32 }}>
                    <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                        <Col xs={24} md={8}>
                            <StatCard
                                title="Total Assets"
                                value={data.total_assets}
                                prefix="₱"
                                icon={<BankOutlined />}
                                color="#3b82f6"
                            />
                        </Col>
                        <Col xs={24} md={8}>
                            <StatCard
                                title="Retained Earnings"
                                value={data.retained_earnings_calced}
                                prefix="₱"
                                icon={<WalletOutlined />}
                                color="#10b981"
                            />
                        </Col>
                    </Row>

                    <Row gutter={[32, 32]}>
                        <Col span={24}>
                            <Title level={4} style={{ marginBottom: 16, fontWeight: 700 }}>Assets</Title>
                            <Table dataSource={data.assets} columns={[{ title: 'Account', dataIndex: 'name' }, { title: 'Amount', dataIndex: 'balance', align: 'right', render: (val) => `₱${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` }]} pagination={false} size="middle" />
                        </Col>
                        <Col span={12}>
                            <Title level={4} style={{ margin: '24px 0 16px 0', fontWeight: 700 }}>Liabilities</Title>
                            <Table dataSource={data.liabilities} columns={[{ title: 'Account', dataIndex: 'name' }, { title: 'Amount', dataIndex: 'balance', align: 'right', render: (val) => `₱${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` }]} pagination={false} size="middle" />
                        </Col>
                        <Col span={12}>
                            <Title level={4} style={{ margin: '24px 0 16px 0', fontWeight: 700 }}>Equity</Title>
                            <Table dataSource={data.equity} columns={[{ title: 'Account', dataIndex: 'name' }, { title: 'Amount', dataIndex: 'balance', align: 'right', render: (val) => `₱${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}` }]} pagination={false} size="middle" />
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
};

const InventoryValuationTab = () => {
    const [asOfDate, setAsOfDate] = useState(dayjs());
    const [branches, setBranches] = useState([]);
    const [branchId, setBranchId] = useState('all');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchBranches = async () => {
        try {
            const { data } = await client.get('/master/branches');
            setBranches(data);
        } catch (error) {
            message.error('Failed to load branches');
        }
    };

    const generate = async () => {
        setLoading(true);
        try {
            const { data } = await client.get('/transactions/reports/inventory-valuation', {
                params: {
                    as_of_date: asOfDate.format('YYYY-MM-DD'),
                    branch_id: branchId === 'all' ? undefined : branchId,
                }
            });
            setData(data);
        } catch (error) {
            message.error('Failed to generate valuation report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
        generate();
    }, []);

    const exportCsv = () => {
        if (!data?.items?.length) {
            message.info('No data to export');
            return;
        }

        const headers = ['Item', 'Category', 'Branch', 'Stock Level', 'Avg. Cost (WAC)', 'Total Value'];
        const rows = data.items.map((row) => ([
            row.item_name,
            row.category,
            row.branch_name,
            row.quantity_on_hand,
            row.average_cost,
            row.total_value,
        ]));

        const escapeValue = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
        const csv = [headers, ...rows]
            .map((row) => row.map(escapeValue).join(','))
            .join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const dateLabel = asOfDate.format('YYYY-MM-DD');
        const branchLabel = branchId === 'all' ? '' : `-branch-${branchId}`;
        link.href = url;
        link.setAttribute('download', `inventory-valuation-${dateLabel}${branchLabel}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const columns = [
        {
            title: 'Item',
            dataIndex: 'item_name',
            key: 'item_name',
            sorter: (a, b) => a.item_name.localeCompare(b.item_name)
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (cat) => <Tag color="blue">{cat}</Tag>
        },
        {
            title: 'Branch',
            dataIndex: 'branch_name',
            key: 'branch_name'
        },
        {
            title: 'Stock Level',
            dataIndex: 'quantity_on_hand',
            key: 'quantity_on_hand',
            align: 'right',
            render: (val) => Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        {
            title: 'Avg. Cost (WAC)',
            dataIndex: 'average_cost',
            key: 'average_cost',
            align: 'right',
            render: (val) => `₱${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        },
        {
            title: 'Total Value',
            dataIndex: 'total_value',
            key: 'total_value',
            align: 'right',
            render: (val) => <Typography.Text strong>₱{Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography.Text>
        }
    ];

    return (
        <div style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--card-bg)', padding: 24, borderRadius: 16, border: '1px solid rgba(148, 163, 184, 0.2)' }}>
                <div>
                    <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Report Settings</Typography.Text>
                    <Space size="middle">
                        <DatePicker
                            value={asOfDate}
                            onChange={setAsOfDate}
                            placeholder="Valuation As Of..."
                            style={{ height: 40, borderRadius: 8, width: 220 }}
                        />
                        <Select
                            value={branchId}
                            onChange={setBranchId}
                            style={{ height: 40, borderRadius: 8, width: 220 }}
                            options={[
                                { label: 'All Branches', value: 'all' },
                                ...branches.map((branch) => ({
                                    label: branch.name,
                                    value: branch.id,
                                })),
                            ]}
                        />
                        <Button
                            type="primary"
                            icon={<FileTextOutlined />}
                            onClick={generate}
                            loading={loading}
                            style={{ height: 40, borderRadius: 8, fontWeight: 600 }}
                        >
                            Update Valuation
                        </Button>
                        <Button
                            onClick={exportCsv}
                            style={{ height: 40, borderRadius: 8, fontWeight: 600 }}
                        >
                            Export CSV
                        </Button>
                    </Space>
                </div>
                {data && (
                    <div style={{ textAlign: 'right' }}>
                        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Total Inventory Value</Typography.Text>
                        <Title level={2} style={{ margin: 0, color: '#059669', fontWeight: 900 }}>
                            ₱{Number(data.total_valuation).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Title>
                    </div>
                )}
            </div>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    dataSource={data?.items || []}
                    columns={columns}
                    loading={loading}
                    rowKey={(r) => `${r.item_id}-${r.branch_id}`}
                    pagination={{ defaultPageSize: 15, showSizeChanger: true, pageSizeOptions: ['10', '15', '20', '50', '100'] }}
                />
            </Card>
        </div>
    );
};

const PeriodManagementTab = ({ periods, loading, onCreate, onToggle }) => {
    const columns = [
        { title: 'Start Date', dataIndex: 'period_start', render: (d) => dayjs(d).format('MMM D, YYYY') },
        { title: 'End Date', dataIndex: 'period_end', render: (d) => dayjs(d).format('MMM D, YYYY') },
        { title: 'Status', dataIndex: 'status', render: (s) => <Tag color={s === 'OPEN' ? 'green' : 'red'} style={{ borderRadius: 12, padding: '0 12px' }}>{s}</Tag> },
        { title: 'Locker', dataIndex: ['locker', 'name'], render: (name) => name || 'SYSTEM' },
        {
            title: 'Action', align: 'right', render: (_, r) => (
                <Button
                    size="middle"
                    danger={r.status === 'OPEN'}
                    icon={r.status === 'OPEN' ? <LockOutlined /> : <UnlockOutlined />}
                    onClick={() => onToggle(r.id, r.status)}
                    style={{ borderRadius: 8 }}
                >
                    {r.status === 'OPEN' ? 'Close Period' : 'Reopen'}
                </Button>
            )
        }
    ];

    return (
        <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={onCreate}
                    style={{ height: 40, borderRadius: 8, fontWeight: 600 }}
                >
                    New Accounting Period
                </Button>
            </div>
            <Table dataSource={periods} columns={columns} loading={loading} rowKey="id" pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} />
        </div>
    );
};

const ReportsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [periodLoading, setPeriodLoading] = useState(false);
    const [periods, setPeriods] = useState([]);
    const [isPeriodModalVisible, setIsPeriodModalVisible] = useState(false);

    useEffect(() => {
        fetchPeriods();
    }, []);

    const fetchPeriods = async () => {
        setPeriodLoading(true);
        try {
            const { data } = await client.get('/transactions/reports/periods');
            setPeriods(data);
        } catch (error) {
            console.error(error);
        } finally {
            setPeriodLoading(false);
        }
    };

    const createPeriod = async (values) => {
        try {
            await client.post('/transactions/reports/periods', {
                period_start: values.range[0].format('YYYY-MM-DD'),
                period_end: values.range[1].format('YYYY-MM-DD'),
            });
            message.success('Accounting Period Created');
            setIsPeriodModalVisible(false);
            fetchPeriods();
        } catch (error) {
            message.error('Failed to create period');
        }
    };

    const togglePeriodStatus = async (id, status) => {
        try {
            const action = status === 'OPEN' ? 'close' : 'reopen';
            await client.post(`/transactions/reports/periods/${id}/${action}`);
            message.success(`Period ${status === 'OPEN' ? 'Closed' : 'Reopened'}`);
            fetchPeriods();
        } catch (error) {
            message.error('Failed to update period status');
        }
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32 }}>
                <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Reporting & Control</Title>
                <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Generate financial statements and manage accounting periods.</Typography.Text>
            </div>

            <Card className="premium-card" bodyStyle={{ padding: isMobile ? '0 12px 16px 12px' : '0 24px 24px 24px' }}>
                <Tabs
                    defaultActiveKey="1"
                    size="large"
                    items={[
                        {
                            key: '1',
                            label: <span><BarChartOutlined style={{ marginRight: 8 }} />Financial Statements</span>,
                            children: <FinancialReportsTab />
                        },
                        {
                            key: '2',
                            label: <span><AppstoreOutlined style={{ marginRight: 8 }} />Inventory Valuation</span>,
                            children: <InventoryValuationTab />
                        },
                        {
                            key: '3',
                            label: <span><CalendarOutlined style={{ marginRight: 8 }} />Period Management</span>,
                            children: <PeriodManagementTab
                                periods={periods}
                                loading={periodLoading}
                                onCreate={() => setIsPeriodModalVisible(true)}
                                onToggle={togglePeriodStatus}
                            />
                        }
                    ]}
                />
            </Card>

            <Modal
                title="Create Accounting Period"
                open={isPeriodModalVisible}
                onCancel={() => setIsPeriodModalVisible(false)}
                footer={null}
                width={500}
            >
                <Form onFinish={createPeriod} layout="vertical">
                    <Form.Item name="range" label="Period Range" rules={[{ required: true }]}>
                        <RangePicker style={{ width: '100%', height: 45, borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block style={{ height: 45, borderRadius: 8, fontWeight: 600 }}>
                            Create Period
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ReportsPage;
